import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  Input,
  PrimaryButton,
  SecondaryButton,
  TitleText,
} from "../../common/native";
import { SessionContext } from "../../../contexts";
import {
  BillingAdminApiError,
  TBillingAdminAuth,
  TBillingAdminClinicSummary,
  TClinicActivityStreamEvent,
  TClinicActivityStreamResponse,
  getBillingAdminSummary,
  getBillingAdminClinicActivityStream,
} from "../../../services/api-billing-admin";
import { ADMIN_TOKENS } from "../../../styles/adminTokens";
import AdminAnalyticsTabs from "./AdminAnalyticsTabs";

const TOKENS = ADMIN_TOKENS;

const DATE_PRESET_OPTIONS = [
  "Month to date (MTD)",
  "This month",
  "Last month",
  "Last 30 days",
  "Last 60 days",
  "Last 90 days",
  "Last 6 months",
  "Last 12 months",
];

const formatDateInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getPresetDateRange = (preset: string) => {
  const today = new Date();
  const endDate = formatDateInput(today);

  switch (preset) {
    case "Month to date (MTD)":
    case "This month": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { startDate: formatDateInput(start), endDate };
    }
    case "Last month": {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return {
        startDate: formatDateInput(start),
        endDate: formatDateInput(end),
      };
    }
    case "Last 30 days": {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return { startDate: formatDateInput(start), endDate };
    }
    case "Last 60 days": {
      const start = new Date(today);
      start.setDate(start.getDate() - 59);
      return { startDate: formatDateInput(start), endDate };
    }
    case "Last 90 days": {
      const start = new Date(today);
      start.setDate(start.getDate() - 89);
      return { startDate: formatDateInput(start), endDate };
    }
    case "Last 6 months": {
      const start = new Date(today);
      start.setMonth(start.getMonth() - 6);
      return { startDate: formatDateInput(start), endDate };
    }
    case "Last 12 months": {
      const start = new Date(today);
      start.setMonth(start.getMonth() - 12);
      return { startDate: formatDateInput(start), endDate };
    }
    default:
      return { startDate: "", endDate: "" };
  }
};

const parseDateOnly = (dateString?: string) => {
  if (!dateString) return null;
  const [year, month, day] = dateString
    .split("-")
    .map((value) => Number(value));
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const formatDateOnly = (dateString?: string) => {
  const date = parseDateOnly(dateString);
  if (!date) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatDateTime = (dateString?: string) => {
  if (!dateString) return { local: "—", utc: "—" };
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return { local: dateString, utc: "—" };
  return {
    local: date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }),
    utc: date.toUTCString(),
  };
};

const WEB_DATE_INPUT_STYLE: React.CSSProperties = {
  height: TOKENS.size.inputHeight,
  borderRadius: TOKENS.radius.md,
  border: `${TOKENS.borderWidth.sm}px solid ${TOKENS.color.borderInput}`,
  padding: `${TOKENS.spacing.sm}px ${TOKENS.spacing.md}px`,
  fontFamily: TOKENS.font.medium,
  fontSize: TOKENS.fontSize.md,
  color: TOKENS.color.textPrimary,
  backgroundColor: TOKENS.color.surface,
};

const DateInput: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, value, onChange }) => {
  if (Platform.OS === "web") {
    return (
      <View style={styles.dateField}>
        <Text style={styles.dateFieldLabel}>{label}</Text>
        <input
          type="date"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          style={WEB_DATE_INPUT_STYLE}
        />
      </View>
    );
  }

  return (
    <View style={styles.dateField}>
      <Input
        name={label}
        placeholder="YYYY-MM-DD"
        value={value}
        onChangeText={onChange}
      />
    </View>
  );
};

const formatEventLabel = (value?: string) => {
  if (!value) return "Unknown";
  return value
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const EVENT_DETAIL_KEYS_BY_TYPE: Record<string, string[]> = {
  simulation: ["simulation_id", "session_id"],
  transform_image: [
    "image_id",
    "upload_type",
    "procedure_types",
    "view_type",
    "visit_uuid",
    "patient_uuid",
    "uploaded",
  ],
  image_upload: [
    "image_id",
    "upload_type",
    "procedure_types",
    "view_type",
    "visit_uuid",
    "patient_uuid",
    "uploaded",
  ],
  side_by_side: [
    "side_by_side_id",
    "batch_uuid",
    "processing_type",
    "uploaded",
    "patient_uuid",
  ],
  note: ["note_id", "note_type", "patient_uuid", "appointment_id"],
  audio: ["audio_id", "is_uploaded", "type", "patient_uuid"],
};

const getDetailValue = (
  event: TClinicActivityStreamEvent,
  key: string,
): unknown => event.details?.[key];

const getPrimaryMetaDetails = (event: TClinicActivityStreamEvent) => {
  const patient = getDetailValue(event, "patient_uuid");
  const visit = getDetailValue(event, "visit_uuid");
  const appointment = getDetailValue(event, "appointment_id");
  const eventKeys = EVENT_DETAIL_KEYS_BY_TYPE[event.event_type] || [];
  const identifiers = eventKeys
    .filter(
      (key) => !["patient_uuid", "visit_uuid", "appointment_id"].includes(key),
    )
    .map((key) => [key, getDetailValue(event, key)] as const)
    .filter(([, value]) => value !== null && value !== undefined)
    .slice(0, 2);

  return { patient, visit, appointment, identifiers };
};

const getEventDetails = (event: TClinicActivityStreamEvent) => {
  const eventKeys = EVENT_DETAIL_KEYS_BY_TYPE[event.event_type] || [];
  if (eventKeys.length) {
    return eventKeys
      .map((key) => [key, event.details?.[key]] as const)
      .filter(([, value]) => value !== null && value !== undefined)
      .slice(0, 6);
  }
  return Object.entries(event.details || {})
    .filter(([, value]) => value !== null && value !== undefined)
    .slice(0, 6);
};

const formatDetailValue = (value: unknown) => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);

  try {
    const json = JSON.stringify(value);
    if (!json) return "—";
    return json.length > 160 ? `${json.slice(0, 157)}...` : json;
  } catch {
    return String(value);
  }
};

const AdminActivityStreamPage: React.FC = () => {
  const sessionContext = useContext(SessionContext);
  const session = sessionContext?.session;

  const [activeAuthMode, setActiveAuthMode] = useState<"apiKey" | "jwt">(
    "apiKey",
  );
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [authSecret, setAuthSecret] = useState<TBillingAdminAuth | null>(null);

  const [clinicId, setClinicId] = useState("");
  const [clinicSearch, setClinicSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [datePreset, setDatePreset] = useState("");
  const [isQuickRangeModalVisible, setIsQuickRangeModalVisible] =
    useState(false);
  const [limit, setLimit] = useState("200");
  const [eventTypeFilter, setEventTypeFilter] = useState("");
  const [searchFilter, setSearchFilter] = useState("");

  const [stream, setStream] = useState<TClinicActivityStreamResponse | null>(
    null,
  );
  const [events, setEvents] = useState<TClinicActivityStreamEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [showRawEventJson, setShowRawEventJson] = useState<
    Record<string, boolean>
  >({});

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [clinicOptions, setClinicOptions] = useState<
    TBillingAdminClinicSummary[]
  >([]);
  const [clinicOptionsError, setClinicOptionsError] = useState<string | null>(
    null,
  );
  const [isLoadingClinics, setIsLoadingClinics] = useState(false);

  const requestIdRef = useRef(0);
  const clinicsRequestIdRef = useRef(0);

  const handleAuthFailure = (error: BillingAdminApiError) => {
    if (error.status === 401 || error.status === 403) {
      setAuthSecret(null);
    }
  };

  const loadClinics = useCallback(async (auth: TBillingAdminAuth) => {
    clinicsRequestIdRef.current += 1;
    const requestId = clinicsRequestIdRef.current;
    setIsLoadingClinics(true);
    setClinicOptionsError(null);

    try {
      const response = await getBillingAdminSummary(auth);
      if (requestId !== clinicsRequestIdRef.current) return;
      setClinicOptions(response.clinics || []);
    } catch (error) {
      if (requestId !== clinicsRequestIdRef.current) return;
      setClinicOptions([]);
      if (error instanceof BillingAdminApiError) {
        handleAuthFailure(error);
        setClinicOptionsError(error.message);
      } else if (error instanceof Error) {
        setClinicOptionsError(error.message);
      } else {
        setClinicOptionsError("Failed to load clinic list.");
      }
    } finally {
      if (requestId === clinicsRequestIdRef.current) {
        setIsLoadingClinics(false);
      }
    }
  }, []);

  const loadStream = useCallback(
    async ({ cursor, append }: { cursor?: string; append?: boolean }) => {
      if (!authSecret) {
        setErrorMessage("Connect with an admin credential first.");
        return;
      }
      const trimmedClinicId = clinicId.trim();
      if (!trimmedClinicId) {
        setErrorMessage("Enter a clinic UUID to load the activity stream.");
        return;
      }
      if (startDate && endDate && startDate > endDate) {
        setErrorMessage(
          "Start date must be earlier than or equal to end date.",
        );
        return;
      }

      requestIdRef.current += 1;
      const requestId = requestIdRef.current;

      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }
      setErrorMessage(null);

      try {
        const parsedLimit = Number(limit);
        const response = await getBillingAdminClinicActivityStream(authSecret, {
          clinicId: trimmedClinicId,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          limit: Number.isFinite(parsedLimit) ? parsedLimit : undefined,
          cursor: cursor || undefined,
        });

        if (requestId !== requestIdRef.current) return;

        setStream(response);
        setNextCursor(response.next_cursor ?? null);
        if (append) {
          setEvents((prev) => [...prev, ...response.events]);
        } else {
          setEvents(response.events);
        }
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        if (error instanceof BillingAdminApiError) {
          handleAuthFailure(error);
          setErrorMessage(error.message);
        } else if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage("Failed to load clinic activity stream.");
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [authSecret, clinicId, startDate, endDate, limit],
  );

  const handleConnectWithApiKey = () => {
    const trimmed = apiKeyInput.trim();
    if (!trimmed) {
      setErrorMessage("Enter your billing admin key to connect.");
      return;
    }
    setStream(null);
    setEvents([]);
    setNextCursor(null);
    setErrorMessage(null);
    setAuthSecret({ type: "apiKey", secret: trimmed });
  };

  const handleConnectWithJwt = () => {
    const token = session?.accessToken;
    if (!token) {
      setErrorMessage("Sign in with an admin session to use JWT auth.");
      return;
    }
    setStream(null);
    setEvents([]);
    setNextCursor(null);
    setErrorMessage(null);
    setAuthSecret({ type: "jwt", secret: token });
  };

  const handleReset = () => {
    setAuthSecret(null);
    setApiKeyInput("");
    setClinicId("");
    setClinicSearch("");
    setStartDate("");
    setEndDate("");
    setDatePreset("");
    setLimit("200");
    setEventTypeFilter("");
    setSearchFilter("");
    setStream(null);
    setEvents([]);
    setNextCursor(null);
    setIsLoading(false);
    setIsLoadingMore(false);
    setErrorMessage(null);
    setClinicOptions([]);
    setClinicOptionsError(null);
    setIsLoadingClinics(false);
  };

  const activeAuthBadge = authSecret
    ? authSecret.type === "apiKey"
      ? "Billing admin key"
      : "Admin JWT"
    : "Disconnected";

  React.useEffect(() => {
    if (!authSecret) return;
    loadClinics(authSecret);
  }, [authSecret, loadClinics]);

  const clinicSearchResults = useMemo(() => {
    const term = clinicSearch.trim().toLowerCase();
    if (!term) return clinicOptions.slice(0, 8);
    return clinicOptions
      .filter((clinic) => {
        const name = clinic.clinic_name?.toLowerCase() || "";
        const uuid = clinic.clinic_uuid?.toLowerCase() || "";
        return name.includes(term) || uuid.includes(term);
      })
      .slice(0, 8);
  }, [clinicOptions, clinicSearch]);

  const filteredEvents = useMemo(() => {
    const trimmedType = eventTypeFilter
      .split(",")
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean);

    const search = searchFilter.trim().toLowerCase();

    return events.filter((event) => {
      if (trimmedType.length) {
        if (!trimmedType.includes(event.event_type?.toLowerCase() || "")) {
          return false;
        }
      }

      if (search) {
        const detailString = JSON.stringify(event.details || {}).toLowerCase();
        const haystack = [
          event.event_id,
          event.event_type,
          event.source,
          detailString,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!haystack.includes(search)) {
          return false;
        }
      }

      return true;
    });
  }, [events, eventTypeFilter, searchFilter]);

  const renderExpandedJson = (event: TClinicActivityStreamEvent) => {
    try {
      return JSON.stringify(event.details || {}, null, 2);
    } catch {
      return String(event.details || {});
    }
  };

  const renderRawEventJson = (event: TClinicActivityStreamEvent) => {
    try {
      return JSON.stringify(event, null, 2);
    } catch {
      return String(event);
    }
  };

  return (
    <ScrollView style={styles.page} contentContainerStyle={styles.pageContent}>
      <View style={styles.headerRow}>
        <View style={styles.headerTextGroup}>
          <TitleText text="Clinic Activity Stream" />
          <Text style={styles.subtitle}>
            Inspect individual events across transforms, uploads, notes, audio,
            and side-by-side images.
          </Text>
        </View>
        <View style={styles.badgeWrapper}>
          <Text style={styles.authBadgeLabel}>Auth mode</Text>
          <Text style={[styles.authBadge, !authSecret && styles.authBadgeIdle]}>
            {activeAuthBadge}
          </Text>
        </View>
      </View>
      <AdminAnalyticsTabs activeTab="activity" />

      <View style={styles.card}>
        <Text style={styles.cardTitle}>Connection</Text>
        <Text style={styles.cardSubtitle}>
          Authenticate with either a billing admin key or an admin bearer
          session.
        </Text>

        <View style={styles.authToggleRow}>
          <SecondaryButton
            text="Billing Admin Key"
            onPress={() => setActiveAuthMode("apiKey")}
            selected={activeAuthMode === "apiKey"}
            style={styles.authToggleButton}
          />
          <SecondaryButton
            text="Admin JWT"
            onPress={() => setActiveAuthMode("jwt")}
            selected={activeAuthMode === "jwt"}
            style={styles.authToggleButton}
          />
        </View>

        {activeAuthMode === "apiKey" ? (
          <View style={styles.authContent}>
            <Input
              name="Billing Admin Key"
              placeholder="Paste admin key"
              value={apiKeyInput}
              onChangeText={setApiKeyInput}
              autoCapitalize="none"
              secureTextEntry
            />
            <PrimaryButton
              text="Connect with key"
              onPress={handleConnectWithApiKey}
            />
          </View>
        ) : (
          <View style={styles.authContent}>
            <Text style={styles.jwtHint}>
              {session?.accessToken
                ? `Using current session token (${session.userEmail}).`
                : "Sign in to an admin account to use JWT auth."}
            </Text>
            <PrimaryButton
              text="Use current admin session"
              onPress={handleConnectWithJwt}
              disabled={!session?.accessToken}
            />
          </View>
        )}

        <SecondaryButton
          text="Reset credentials and filters"
          onPress={handleReset}
        />
      </View>

      {authSecret && (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Filters</Text>
          <Text style={styles.cardSubtitle}>
            Clinic ID is required. Leave dates blank for the backend default
            30-day period.
          </Text>
          <View style={styles.filtersRow}>
            <View style={styles.inputField}>
              <Input
                name="Clinic name"
                placeholder="Search clinic name"
                value={clinicSearch}
                onChangeText={setClinicSearch}
                autoCapitalize="none"
              />
            </View>
            <View style={styles.inputField}>
              <Input
                name="Clinic UUID"
                placeholder="Paste clinic UUID"
                value={clinicId}
                onChangeText={setClinicId}
                autoCapitalize="none"
              />
            </View>
            <View style={styles.inputField}>
              <Input
                name="Limit"
                placeholder="200"
                value={limit}
                onChangeText={setLimit}
                keyboardType="numeric"
              />
            </View>
          </View>
          <View style={styles.searchResults}>
            {clinicOptionsError && (
              <Text style={styles.inlineErrorText}>{clinicOptionsError}</Text>
            )}
            {isLoadingClinics && (
              <Text style={styles.inlineHintText}>Loading clinics…</Text>
            )}
            {!isLoadingClinics && clinicSearchResults.length === 0 && (
              <Text style={styles.inlineHintText}>
                No clinics match the current search.
              </Text>
            )}
            {clinicSearchResults.map((clinic) => (
              <View key={clinic.clinic_uuid} style={styles.searchResultRow}>
                <View style={styles.searchResultText}>
                  <Text style={styles.searchResultTitle}>
                    {clinic.clinic_name || "Unnamed clinic"}
                  </Text>
                  <Text style={styles.searchResultSubtitle}>
                    {clinic.clinic_uuid}
                  </Text>
                </View>
                <SecondaryButton
                  text="Use"
                  onPress={() => {
                    setClinicId(clinic.clinic_uuid);
                    setClinicSearch(clinic.clinic_name || "");
                  }}
                  style={styles.searchResultButton}
                />
              </View>
            ))}
          </View>
          <View style={styles.filtersRow}>
            <DateInput
              label="Start date"
              value={startDate}
              onChange={(value) => {
                setStartDate(value);
                setDatePreset("");
              }}
            />
            <DateInput
              label="End date"
              value={endDate}
              onChange={(value) => {
                setEndDate(value);
                setDatePreset("");
              }}
            />
            <View style={styles.datePresetField}>
              <View style={styles.dateField}>
                <Text style={styles.dateFieldLabel}>Quick range</Text>
                <Pressable
                  style={styles.quickRangeButton}
                  onPress={() => setIsQuickRangeModalVisible(true)}
                >
                  <Text
                    style={[
                      styles.quickRangeButtonText,
                      !datePreset && styles.quickRangeButtonTextPlaceholder,
                    ]}
                  >
                    {datePreset || "Custom range"}
                  </Text>
                </Pressable>
              </View>
            </View>
          </View>
          <View style={styles.filtersRow}>
            <View style={styles.inputField}>
              <Input
                name="Event types"
                placeholder="simulation, note, audio"
                value={eventTypeFilter}
                onChangeText={setEventTypeFilter}
                autoCapitalize="none"
              />
            </View>
            <View style={styles.inputField}>
              <Input
                name="Search"
                placeholder="Patient, visit, appointment"
                value={searchFilter}
                onChangeText={setSearchFilter}
                autoCapitalize="none"
              />
            </View>
          </View>
          <View style={styles.buttonRow}>
            <SecondaryButton
              text="Clear filters"
              onPress={() => {
                setStartDate("");
                setEndDate("");
                setEventTypeFilter("");
                setSearchFilter("");
              }}
              style={styles.inlineButton}
            />
            <PrimaryButton
              text="Load activity"
              onPress={() => loadStream({})}
              style={styles.inlineButton}
            />
          </View>
        </View>
      )}

      {errorMessage && !isLoading && (
        <View style={styles.errorCard}>
          <Text style={styles.errorTitle}>Unable to load activity stream</Text>
          <Text style={styles.errorMessage}>{errorMessage}</Text>
        </View>
      )}

      {(isLoading || isLoadingMore) && (
        <View style={styles.loaderRow}>
          <ActivityIndicator color={TOKENS.color.accent} />
          <Text style={styles.loaderText}>
            {isLoadingMore ? "Loading more events…" : "Loading activity…"}
          </Text>
        </View>
      )}

      {stream && !isLoading && (
        <View style={styles.periodCard}>
          <Text style={styles.periodText}>
            Period: {formatDateOnly(stream.start_date)} to{" "}
            {formatDateOnly(stream.end_date)}
          </Text>
        </View>
      )}

      {authSecret && !isLoading && filteredEvents.length === 0 && stream && (
        <View style={styles.emptyCard}>
          <Text style={styles.emptyTitle}>No activity found</Text>
          <Text style={styles.emptyText}>
            Adjust the filters or expand the date range to see more results.
          </Text>
        </View>
      )}

      <Modal
        transparent
        animationType="fade"
        visible={isQuickRangeModalVisible}
        onRequestClose={() => setIsQuickRangeModalVisible(false)}
      >
        <Pressable
          style={styles.quickRangeModalOverlay}
          onPress={() => setIsQuickRangeModalVisible(false)}
        />
        <View style={styles.quickRangeModalCard}>
          <Text style={styles.quickRangeModalTitle}>Quick range</Text>
          <ScrollView style={styles.quickRangeOptionsList}>
            {DATE_PRESET_OPTIONS.map((preset) => {
              const selected = preset === datePreset;
              return (
                <Pressable
                  key={preset}
                  style={[
                    styles.quickRangeOptionButton,
                    selected && styles.quickRangeOptionButtonSelected,
                  ]}
                  onPress={() => {
                    setDatePreset(preset);
                    const range = getPresetDateRange(preset);
                    setStartDate(range.startDate);
                    setEndDate(range.endDate);
                    setIsQuickRangeModalVisible(false);
                  }}
                >
                  <Text
                    style={[
                      styles.quickRangeOptionText,
                      selected && styles.quickRangeOptionTextSelected,
                    ]}
                  >
                    {preset}
                  </Text>
                </Pressable>
              );
            })}
          </ScrollView>
          <SecondaryButton
            text="Close"
            onPress={() => setIsQuickRangeModalVisible(false)}
          />
        </View>
      </Modal>

      {filteredEvents.length > 0 && (
        <View style={styles.list}>
          {filteredEvents.map((event) => {
            const details = getEventDetails(event);
            const primaryDetails = getPrimaryMetaDetails(event);
            const isExpanded = expandedEventId === event.event_id;
            const createdAt = formatDateTime(event.created_at);
            return (
              <Pressable
                key={event.event_id}
                onPress={() =>
                  setExpandedEventId((prev) =>
                    prev === event.event_id ? null : event.event_id,
                  )
                }
              >
                <View style={styles.eventCard}>
                  <View style={styles.eventHeader}>
                    <Text style={styles.eventType}>
                      {formatEventLabel(event.event_type)}
                    </Text>
                    <Text
                      style={styles.eventTime}
                      {...(Platform.OS === "web"
                        ? {
                            // @ts-ignore react-native-web prop
                            title: createdAt.utc,
                          }
                        : {})}
                    >
                      {createdAt.local}
                    </Text>
                  </View>
                  <Text style={styles.eventId}>{event.event_id}</Text>
                  <Text style={styles.eventSource}>Source: {event.source}</Text>
                  <View style={styles.eventMetaRow}>
                    {primaryDetails.patient !== null &&
                      primaryDetails.patient !== undefined && (
                        <Text style={styles.eventMetaText}>
                          Patient: {formatDetailValue(primaryDetails.patient)}
                        </Text>
                      )}
                    {primaryDetails.visit !== null &&
                      primaryDetails.visit !== undefined && (
                        <Text style={styles.eventMetaText}>
                          Visit: {formatDetailValue(primaryDetails.visit)}
                        </Text>
                      )}
                    {primaryDetails.appointment !== null &&
                      primaryDetails.appointment !== undefined && (
                        <Text style={styles.eventMetaText}>
                          Appointment:{" "}
                          {formatDetailValue(primaryDetails.appointment)}
                        </Text>
                      )}
                    {primaryDetails.identifiers.map(([key, value]) => (
                      <Text style={styles.eventMetaText} key={key}>
                        {formatEventLabel(key)}: {formatDetailValue(value)}
                      </Text>
                    ))}
                  </View>
                  {isExpanded ? (
                    <View style={styles.eventDetailsExpanded}>
                      <View style={styles.eventJsonBlock}>
                        <Text style={styles.eventJsonTitle}>Details JSON</Text>
                        <Text style={styles.eventJsonText}>
                          {renderExpandedJson(event)}
                        </Text>
                      </View>
                      <SecondaryButton
                        text={
                          showRawEventJson[event.event_id]
                            ? "Hide raw event"
                            : "Show raw event"
                        }
                        onPress={() =>
                          setShowRawEventJson((current) => ({
                            ...current,
                            [event.event_id]: !current[event.event_id],
                          }))
                        }
                        style={styles.inlineButton}
                      />
                      {showRawEventJson[event.event_id] && (
                        <View style={styles.eventJsonBlock}>
                          <Text style={styles.eventJsonTitle}>
                            Raw event JSON
                          </Text>
                          <Text style={styles.eventJsonText}>
                            {renderRawEventJson(event)}
                          </Text>
                        </View>
                      )}
                    </View>
                  ) : (
                    details.length > 0 && (
                      <View style={styles.eventDetails}>
                        {details.map(([key, value]) => (
                          <Text key={key} style={styles.eventDetailText}>
                            {formatEventLabel(key)}: {formatDetailValue(value)}
                          </Text>
                        ))}
                      </View>
                    )
                  )}
                </View>
              </Pressable>
            );
          })}
        </View>
      )}

      {nextCursor && (
        <View style={styles.loadMoreRow}>
          <PrimaryButton
            text="Load more"
            onPress={() => loadStream({ cursor: nextCursor, append: true })}
            disabled={isLoadingMore}
          />
          <Text style={styles.loadMoreHint}>
            Loaded {events.length} events
            {limit.trim() ? ` (page size ${limit.trim()})` : ""}. Next cursor:{" "}
            {nextCursor}
          </Text>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  page: {
    backgroundColor: TOKENS.color.background,
    flex: 1,
  },
  pageContent: {
    padding: TOKENS.spacing.xxl,
    gap: TOKENS.spacing.lg,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: TOKENS.spacing.lg,
    flexWrap: "wrap",
  },
  headerTextGroup: {
    gap: TOKENS.spacing.sm,
    flexShrink: 1,
  },
  subtitle: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.md,
    color: TOKENS.color.textSecondary,
    maxWidth: TOKENS.size.contentMaxWidth,
  },
  badgeWrapper: {
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.lg,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.md,
    minWidth: TOKENS.size.badgeMinWidth,
    gap: TOKENS.spacing.xs,
  },
  authBadgeLabel: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textTertiary,
  },
  authBadge: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.accentDark,
  },
  authBadgeIdle: {
    color: TOKENS.color.idle,
  },
  card: {
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.xl,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.lg,
    gap: TOKENS.spacing.md,
  },
  cardTitle: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.xl,
    color: TOKENS.color.textPrimary,
  },
  cardSubtitle: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textMuted,
  },
  authToggleRow: {
    flexDirection: "row",
    gap: TOKENS.spacing.sm,
    flexWrap: "wrap",
  },
  authToggleButton: {
    minWidth: TOKENS.size.authButtonMinWidth,
  },
  authContent: {
    gap: TOKENS.spacing.md,
  },
  jwtHint: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSecondary,
  },
  filtersRow: {
    flexDirection: "row",
    gap: TOKENS.spacing.md,
    flexWrap: "wrap",
  },
  inputField: {
    flex: 1,
    minWidth: TOKENS.size.dateFieldMinWidth,
  },
  searchResults: {
    gap: TOKENS.spacing.sm,
  },
  searchResultRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: TOKENS.spacing.sm,
    padding: TOKENS.spacing.sm,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    borderRadius: TOKENS.radius.md,
    backgroundColor: TOKENS.color.surfaceMuted,
  },
  searchResultText: {
    flex: 1,
    gap: TOKENS.spacing.xs,
  },
  searchResultTitle: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
  },
  searchResultSubtitle: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textSecondary,
  },
  searchResultButton: {
    minWidth: TOKENS.size.searchButtonMinWidth,
  },
  inlineHintText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textMuted,
  },
  inlineErrorText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.errorText,
  },
  dateField: {
    flex: 1,
    minWidth: TOKENS.size.dateFieldMinWidth,
    gap: TOKENS.spacing.xs,
  },
  datePresetField: {
    flex: 1,
    minWidth: TOKENS.size.dateFieldMinWidth,
  },
  quickRangeButton: {
    minHeight: TOKENS.size.inputHeight,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.borderInput,
    borderRadius: TOKENS.radius.md,
    paddingHorizontal: TOKENS.spacing.md,
    justifyContent: "center",
    backgroundColor: TOKENS.color.surface,
  },
  quickRangeButtonText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.md,
    color: TOKENS.color.textPrimary,
  },
  quickRangeButtonTextPlaceholder: {
    color: TOKENS.color.idle,
  },
  quickRangeModalOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: TOKENS.color.textPrimary,
    opacity: 0.2,
  },
  quickRangeModalCard: {
    position: "absolute",
    top: "20%",
    left: TOKENS.spacing.lg,
    right: TOKENS.spacing.lg,
    maxHeight: "60%",
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.lg,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.md,
    gap: TOKENS.spacing.sm,
  },
  quickRangeModalTitle: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.lg,
    color: TOKENS.color.textPrimary,
  },
  quickRangeOptionsList: {
    maxHeight: 280,
  },
  quickRangeOptionButton: {
    minHeight: 44,
    borderRadius: TOKENS.radius.md,
    paddingHorizontal: TOKENS.spacing.md,
    paddingVertical: TOKENS.spacing.sm,
    justifyContent: "center",
  },
  quickRangeOptionButtonSelected: {
    backgroundColor: TOKENS.color.surfaceMuted,
  },
  quickRangeOptionText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.md,
    color: TOKENS.color.textPrimary,
  },
  quickRangeOptionTextSelected: {
    color: TOKENS.color.accentDark,
  },
  dateFieldLabel: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
  },
  buttonRow: {
    flexDirection: "row",
    gap: TOKENS.spacing.sm,
    flexWrap: "wrap",
  },
  inlineButton: {
    minWidth: TOKENS.size.inlineButtonMinWidth,
  },
  loaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: TOKENS.spacing.sm,
    paddingHorizontal: TOKENS.spacing.xs,
  },
  loaderText: {
    fontFamily: TOKENS.font.medium,
    color: TOKENS.color.textSecondary,
  },
  errorCard: {
    backgroundColor: TOKENS.color.errorSurface,
    borderColor: TOKENS.color.errorBorder,
    borderWidth: TOKENS.borderWidth.sm,
    borderRadius: TOKENS.radius.lg,
    padding: TOKENS.spacing.md,
    gap: TOKENS.spacing.xs,
  },
  errorTitle: {
    fontFamily: TOKENS.font.bold,
    color: TOKENS.color.errorText,
    fontSize: TOKENS.fontSize.md,
  },
  errorMessage: {
    fontFamily: TOKENS.font.medium,
    color: TOKENS.color.errorTextStrong,
    fontSize: TOKENS.fontSize.sm,
  },
  periodCard: {
    backgroundColor: TOKENS.color.surfaceAccent,
    borderColor: TOKENS.color.borderAccent,
    borderWidth: TOKENS.borderWidth.sm,
    borderRadius: TOKENS.radius.lg,
    padding: TOKENS.spacing.md,
  },
  periodText: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.accentDark,
  },
  emptyCard: {
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.lg,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.lg,
    gap: TOKENS.spacing.xs,
  },
  emptyTitle: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.md,
    color: TOKENS.color.textPrimary,
  },
  emptyText: {
    fontFamily: TOKENS.font.medium,
    color: TOKENS.color.textMuted,
    fontSize: TOKENS.fontSize.sm,
  },
  list: {
    gap: TOKENS.spacing.md,
  },
  eventCard: {
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.lg,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.lg,
    gap: TOKENS.spacing.sm,
    maxWidth: TOKENS.size.eventCardMaxWidth,
  },
  eventHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: TOKENS.spacing.sm,
  },
  eventType: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.md,
    color: TOKENS.color.textPrimary,
  },
  eventTime: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSecondary,
  },
  eventId: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textMuted,
  },
  eventSource: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textSecondary,
  },
  eventMetaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: TOKENS.spacing.sm,
  },
  eventMetaText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textSecondary,
  },
  eventDetails: {
    gap: TOKENS.spacing.xs,
  },
  eventDetailText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textSecondary,
  },
  eventJsonBlock: {
    backgroundColor: TOKENS.color.surfaceMuted,
    borderRadius: TOKENS.radius.md,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.sm,
  },
  eventJsonTitle: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textPrimary,
    marginBottom: TOKENS.spacing.xs,
  },
  eventDetailsExpanded: {
    gap: TOKENS.spacing.sm,
  },
  eventJsonText: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textSecondary,
    lineHeight: TOKENS.lineHeight.sm,
  },
  loadMoreRow: {
    gap: TOKENS.spacing.sm,
    alignItems: "flex-start",
  },
  loadMoreHint: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textMuted,
  },
});

export default AdminActivityStreamPage;
